import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  MultiSelect,
  Paper,
  Splitter,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { MiniCalendar } from "@mantine/dates";
import {
  IconAdjustmentsHorizontal,
  IconAlertTriangle,
  IconBriefcase,
  IconCalendar,
  IconCircleCheck,
  IconFilterOff,
  IconMessageCircle,
  IconRefresh,
  IconSearch,
  IconSend,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { errorNotification, successNotification } from "~/lib/notifications";
import { trpc } from "~/lib/trpc";
import {
  type AttributionSource,
  attributionOptions,
  attributionParser,
  type ConversionStatus,
  dateParser,
  dealIdsParser,
  dealSearchParser,
  type LeadSource,
  leadSourceOptions,
  leadSourceParser,
  statusOptions,
  statusParser,
  today,
} from "./-components/consts";
import { ConversionCard, type GoogleAdsDecision } from "./-components/conversion-card";
import { DealCard } from "./-components/deal-card";
import { LinkDealModal } from "./-components/link-deal-modal";
import { PaneShell } from "./-components/panel-shell";
import { useLinkedDealHover } from "./-components/use-linked-deal-hover";
import { getAttributionSource, isConversionStatus, isGoogleEligible, isLeadSource } from "./-components/utils";

export const Route = createFileRoute("/_authenticated/conversions/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [date, setDate] = useQueryState("date", dateParser);
  const [attributionSources, setAttributionSources] = useQueryState("attribution", attributionParser);
  const [leadSources, setLeadSources] = useQueryState("leadSource", leadSourceParser);
  const [statuses, setStatuses] = useQueryState("status", statusParser);
  const [dealSearch, setDealSearch] = useQueryState("dealSearch", dealSearchParser);
  const [selectedDealIds, setSelectedDealIds] = useQueryState("selectedDeals", dealIdsParser);
  const [markedDealIds, setMarkedDealIds] = useQueryState("markedDeals", dealIdsParser);
  const [linkingConversionId, setLinkingConversionId] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const queryClient = useQueryClient();
  const selectedDate = date.toISOString().slice(0, 10);

  const conversionsQuery = useQuery({
    ...trpc.conversion.getConversions.queryOptions({ date: selectedDate }),
    refetchInterval: (query) => {
      const conversions = query.state.data;
      const scheduledDelays = conversions
        ?.filter((conversion) => conversion.status === "approved" && conversion.scheduledSubmissionAt !== null)
        .map((conversion) => new Date(conversion.scheduledSubmissionAt as string).getTime() - Date.now());
      const refreshDelays = [...(scheduledDelays ?? [])];
      if (conversions?.some((conversion) => conversion.status === "processing")) refreshDelays.push(60_000);
      if (refreshDelays.length === 0) return false;
      return Math.max(1_000, Math.min(...refreshDelays));
    },
  });
  const dealsQuery = useQuery(trpc.conversion.getDeals.queryOptions({ date: selectedDate }));
  const conversions = conversionsQuery.data ?? [];
  const deals = dealsQuery.data ?? [];
  const { onConversionsPointerLeave, onConversionsPointerOver, registerDealElement } = useLinkedDealHover();
  const hasApprovedConversions = conversions.some((conversion) => conversion.status === "approved");

  useEffect(() => {
    if (!hasApprovedConversions) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, [hasApprovedConversions]);

  const hasActiveFilters = attributionSources.length > 0 || leadSources.length > 0 || statuses.length > 0;

  const filteredConversions = useMemo(
    () =>
      conversions.filter((conversion) => {
        const attribution = getAttributionSource(conversion);
        return (
          (attributionSources.length === 0 || attributionSources.includes(attribution)) &&
          (leadSources.length === 0 || (isLeadSource(conversion.source) && leadSources.includes(conversion.source))) &&
          (statuses.length === 0 || (isConversionStatus(conversion.status) && statuses.includes(conversion.status)))
        );
      }),
    [attributionSources, conversions, leadSources, statuses],
  );

  const filteredDeals = useMemo(() => {
    const search = dealSearch.trim().toLocaleLowerCase();
    if (!search) return deals;

    return deals.filter((deal) => {
      const customerName = deal.customer?.name?.toLocaleLowerCase() ?? "";
      const customerEmail = deal.customer?.email?.toLocaleLowerCase() ?? "";
      return customerName.includes(search) || customerEmail.includes(search);
    });
  }, [dealSearch, deals]);

  const conversionByDealId = useMemo(() => {
    const byDealId = new Map<number, (typeof conversions)[number]>();
    for (const conversion of conversions) {
      if (conversion.dealId !== null) byDealId.set(conversion.dealId, conversion);
    }
    return byDealId;
  }, [conversions]);

  const availableDeals = useMemo(
    () => deals.filter((deal) => !conversionByDealId.has(deal.id)),
    [conversionByDealId, deals],
  );

  const linkDealMutation = useMutation({
    ...trpc.conversion.linkDeal.mutationOptions(),
    onSuccess: async () => {
      setLinkingConversionId(null);
      successNotification("Conversion linked to deal");
      await Promise.all([
        queryClient.invalidateQueries(trpc.conversion.getConversions.queryOptions({ date: selectedDate })),
        queryClient.invalidateQueries(trpc.conversion.getDeals.queryOptions({ date: selectedDate })),
      ]);
    },
    onError: (error) => errorNotification(error.message || "Could not link conversion to deal"),
  });

  const decisionMutation = useMutation({
    ...trpc.conversion.setGoogleAdsDecision.mutationOptions(),
    onSuccess: async (_data, variables) => {
      successNotification(
        variables.decision === "approve"
          ? "Conversion approved for submission"
          : variables.decision === "draft"
            ? "Conversion moved to draft"
            : "Conversion rejected",
      );
      await queryClient.invalidateQueries(trpc.conversion.getConversions.queryOptions({ date: selectedDate }));
    },
    onError: (error) => errorNotification(error.message || "Could not update conversion"),
  });

  const openLinkDeal = useCallback((conversionId: number) => setLinkingConversionId(conversionId), []);
  const setGoogleAdsDecision = useCallback(
    (conversionId: number, decision: GoogleAdsDecision) => {
      decisionMutation.mutate({ conversionId, decision });
    },
    [decisionMutation.mutate],
  );

  const selectedDealIdSet = useMemo(() => new Set(selectedDealIds), [selectedDealIds]);

  const clearFilters = () => {
    void Promise.all([setAttributionSources([]), setLeadSources([]), setStatuses([])]);
  };

  const markSelectedDeals = () => {
    void Promise.all([setMarkedDealIds([...new Set([...markedDealIds, ...selectedDealIds])]), setSelectedDealIds([])]);
  };

  return (
    <Box
      h="100dvh"
      mih={540}
      bg="var(--mantine-color-body)"
      style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Paper
        component="header"
        radius={0}
        px="lg"
        py="md"
        style={{
          flex: "0 0 auto",
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: "1px solid var(--mantine-color-default-border)",
        }}>
        <Flex gap="xl" align="stretch" justify="space-between" wrap="wrap">
          <Group gap="xs">
            <ThemeIcon variant="light" size="lg" radius="md">
              <IconSparkles size={20} />
            </ThemeIcon>
            <Title order={2} size="h3">
              Offline conversions
            </Title>
          </Group>

          <Box style={{ flex: "0 1 auto" }}>
            <Group justify="space-between" mb="xs">
              <Group gap={6}>
                <IconCalendar size={16} />
                <Text size="sm" fw={600}>
                  {dayjs(selectedDate).format("dddd, D MMMM")}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Select date
              </Text>
            </Group>
            <MiniCalendar
              value={selectedDate}
              maxDate={today}
              onChange={(value) => {
                setLinkingConversionId(null);
                void Promise.all([
                  setDate(new Date(`${value}T00:00:00.000Z`)),
                  setSelectedDealIds([]),
                  setMarkedDealIds([]),
                ]);
              }}
              numberOfDays={7}
              getDayProps={(calendarDate) => ({
                style: {
                  color: [0, 6].includes(dayjs(calendarDate).day()) ? "var(--mantine-color-red-text)" : undefined,
                },
              })}
            />
          </Box>
        </Flex>
      </Paper>

      <Box style={{ flex: "1 1 auto", minHeight: 0 }}>
        <Splitter h="100%" lineSize={1} handleColor="gray.7" styles={{ pane: { minWidth: 0 } }}>
          <Splitter.Pane defaultSize={54} min="360px">
            <PaneShell
              title="Conversions"
              subtitle="Raw leads received from the website"
              count={filteredConversions.length}
              icon={<IconAdjustmentsHorizontal size={18} />}
              controls={
                <Group gap="xs" align="flex-end" wrap="wrap">
                  <MultiSelect
                    aria-label="Filter by attribution source"
                    placeholder="Filter by attribution"
                    data={attributionOptions}
                    value={attributionSources}
                    onChange={(value) => void setAttributionSources(value as AttributionSource[])}
                    leftSection={<IconAdjustmentsHorizontal size={17} />}
                    style={{ flex: "1 1 150px", minWidth: 140 }}
                    comboboxProps={{ withinPortal: true }}
                  />
                  <MultiSelect
                    aria-label="Filter by lead type"
                    placeholder="Filter by lead types"
                    data={leadSourceOptions}
                    value={leadSources}
                    onChange={(value) => void setLeadSources(value as LeadSource[])}
                    leftSection={<IconMessageCircle size={17} />}
                    style={{ flex: "1 1 150px", minWidth: 140 }}
                    comboboxProps={{ withinPortal: true }}
                  />
                  <MultiSelect
                    aria-label="Filter by conversion status"
                    placeholder="Filter by status"
                    data={statusOptions}
                    value={statuses}
                    onChange={(value) => void setStatuses(value as ConversionStatus[])}
                    leftSection={<IconCircleCheck size={17} />}
                    style={{ flex: "1 1 150px", minWidth: 140 }}
                  />
                  <Tooltip label="Clear conversion filters" disabled={!hasActiveFilters}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size={36}
                      aria-label="Clear conversion filters"
                      disabled={!hasActiveFilters}
                      onClick={clearFilters}>
                      <IconFilterOff size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              }>
              <Stack
                gap="sm"
                p="md"
                onPointerOver={onConversionsPointerOver}
                onPointerLeave={onConversionsPointerLeave}>
                {conversionsQuery.isPending ? (
                  <LoadingState label="Loading conversions" />
                ) : conversionsQuery.isError ? (
                  <ErrorState label="Could not load conversions" onRetry={() => void conversionsQuery.refetch()} />
                ) : (
                  filteredConversions.map((conversion) => (
                    <ConversionCard
                      key={conversion.id}
                      conversion={conversion}
                      now={now}
                      decisionPending={
                        decisionMutation.isPending && decisionMutation.variables?.conversionId === conversion.id
                      }
                      onLinkDeal={openLinkDeal}
                      onDecision={setGoogleAdsDecision}
                    />
                  ))
                )}
                {!conversionsQuery.isPending && !conversionsQuery.isError && filteredConversions.length === 0 && (
                  <EmptyState title="No conversions found" description="Try clearing one or more filters." />
                )}
              </Stack>
            </PaneShell>
          </Splitter.Pane>

          <Splitter.Pane defaultSize={46} min="340px">
            <PaneShell
              title="Deals"
              subtitle="Choose which deals should be sent to Google Ads"
              count={filteredDeals.length}
              icon={<IconBriefcase size={18} />}
              actions={
                <Button
                  size="xs"
                  leftSection={<IconSend size={15} />}
                  disabled={dealsQuery.isPending || dealsQuery.isError || selectedDealIds.length === 0}
                  onClick={markSelectedDeals}>
                  Mark {selectedDealIds.length || "selected"}
                </Button>
              }
              controls={
                <TextInput
                  aria-label="Search deals by customer name or email"
                  placeholder="Search customer name or email"
                  value={dealSearch}
                  onChange={(event) => void setDealSearch(event.currentTarget.value)}
                  leftSection={<IconSearch size={17} />}
                  rightSection={
                    dealSearch ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label="Clear deal search"
                        onClick={() => void setDealSearch("")}>
                        <IconX size={14} />
                      </ActionIcon>
                    ) : null
                  }
                />
              }>
              <Box
                p="md"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 240px), 1fr))",
                  gap: "var(--mantine-spacing-sm)",
                  alignItems: "start",
                  maxWidth: "calc(5 * 280px + 4 * var(--mantine-spacing-sm))",
                }}>
                {dealsQuery.isPending ? (
                  <Box style={{ gridColumn: "1 / -1" }}>
                    <LoadingState label="Loading deals" />
                  </Box>
                ) : dealsQuery.isError ? (
                  <Box style={{ gridColumn: "1 / -1" }}>
                    <ErrorState label="Could not load deals" onRetry={() => void dealsQuery.refetch()} />
                  </Box>
                ) : null}
                {!dealsQuery.isPending &&
                  !dealsQuery.isError &&
                  filteredDeals.map((deal) => {
                    const conversion = conversionByDealId.get(deal.id);
                    const isEligible = isGoogleEligible(conversion);
                    const isSelected = selectedDealIdSet.has(deal.id);

                    return (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        conversion={conversion}
                        eligible={isEligible}
                        selected={isSelected}
                        registerElement={registerDealElement}
                      />
                    );
                  })}
                {!dealsQuery.isPending && !dealsQuery.isError && filteredDeals.length === 0 && (
                  <Box style={{ gridColumn: "1 / -1" }}>
                    <EmptyState title="No deals found" description="No customers match this name or email search." />
                  </Box>
                )}
              </Box>
            </PaneShell>
          </Splitter.Pane>
        </Splitter>
      </Box>

      <LinkDealModal
        opened={linkingConversionId !== null}
        deals={availableDeals}
        pendingDealId={linkDealMutation.isPending ? linkDealMutation.variables?.dealId : undefined}
        onClose={() => {
          if (!linkDealMutation.isPending) setLinkingConversionId(null);
        }}
        onSelect={(dealId) => {
          if (linkingConversionId !== null) {
            linkDealMutation.mutate({ conversionId: linkingConversionId, dealId });
          }
        }}
      />
    </Box>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <Stack align="center" gap="sm" py={60} px="md">
      <Loader size="sm" />
      <Text size="sm" c="dimmed">
        {label}
      </Text>
    </Stack>
  );
}

function ErrorState({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <Stack align="center" gap="sm" py={60} px="md">
      <ThemeIcon variant="light" color="red" size={44} radius="xl">
        <IconAlertTriangle size={21} />
      </ThemeIcon>
      <Text fw={600}>{label}</Text>
      <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={onRetry}>
        Try again
      </Button>
    </Stack>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <Stack align="center" gap={6} py={60} px="md">
      <ThemeIcon variant="light" color="gray" size={44} radius="xl">
        <IconRefresh size={21} />
      </ThemeIcon>
      <Text fw={600}>{title}</Text>
      <Text size="sm" c="dimmed" ta="center">
        {description}
      </Text>
    </Stack>
  );
}
